import React, { useEffect, useState, useCallback } from "react";
import { FiShoppingBag, FiInfo, FiSearch, FiArrowLeft, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import axios from "../../Utils/axiosInstance";
import { useAuth } from "../../Components/Context/AuthContext";
import { assets } from "../../Assets/assets";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import SupplierOrderListItem from "../../Components/UserSideComponents/SupplierOrders/SupplierOrderListItem";
import SupplierOrderDetailCard from "../../Components/UserSideComponents/SupplierOrders/SupplierOrderDetailCard";
import SupplierIncomingOrdersInfoModal from "../../Components/UserSideComponents/SupplierOrders/SupplierIncomingOrdersInfoModal";

const TABS = [
  { label: "All", stage: null },
  { label: "New", stage: "Order" },
  {
    label: "Dispatched",
    stage: "Order-Fullfillment,Delivery_And_Payment",
  },
  { label: "Completed", stage: "Completed" },
];

const EmptySupplierOrders = ({ activeTab }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <img
      src={assets.noordervector}
      alt="No Live Orders"
      className="w-64 md:w-80 mb-5 opacity-90"
    />
    <h2 className="text-xl font-semibold text-gray-700">
      No {activeTab !== "All" ? activeTab : "Live"} Orders Found
    </h2>
    <p className="text-sm text-gray-400 mt-1 max-w-sm">
      {activeTab === "New"
        ? "No new incoming orders currently available for your linked varieties."
        : activeTab === "Dispatched"
          ? "You don't have any orders in the fulfillment or dispatch stage right now."
          : activeTab === "Completed"
            ? "No completed orders to show yet."
            : "No active buyer orders matching your linked varieties at this time."}
    </p>
  </div>
);

const SupplierOrders = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("All");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const fetchSupplierOrders = useCallback(
    async (currentPage = 1) => {
      const supplierId = user?._id || user?.id;
      if (!supplierId) return;

      try {
        setLoading(true);
        setError(false);

        const currentTabObj = TABS.find((t) => t.label === activeTab);
        let url = `/order/fetch-orders-by-linked-supplier/${supplierId}?page=${currentPage}&limit=${limit}`;

        if (currentTabObj?.stage) {
          url += `&stages=${currentTabObj.stage}`;
        }

        const { data } = await axios.get(url);

        if (data.success) {
          const fetchedOrders = data.data || [];
          setOrders(fetchedOrders);
          setPagination(data.pagination || null);

          // Deep-link / target orderId check
          const params = new URLSearchParams(location.search);
          const targetOrderId = params.get("orderId") || params.get("highlight");
          if (targetOrderId && fetchedOrders.length > 0) {
            const found = fetchedOrders.find(
              (o) => o._id === targetOrderId || o.orderNumber === targetOrderId
            );
            if (found) {
              setSelectedOrder(found);
              return;
            }
          }

          // Auto-select first order on desktop
          if (fetchedOrders.length > 0 && window.innerWidth >= 768) {
            setSelectedOrder((prev) => {
              if (prev) {
                const stillExists = fetchedOrders.find((o) => o._id === prev._id);
                return stillExists || fetchedOrders[0];
              }
              return fetchedOrders[0];
            });
          } else if (fetchedOrders.length === 0) {
            setSelectedOrder(null);
          }
        } else {
          setError(true);
          toast.error(data.message || "Failed to fetch supplier orders");
        }
      } catch (err) {
        console.error("fetchSupplierOrders error:", err);
        setError(true);
        toast.error("Failed to load live orders");
      } finally {
        setLoading(false);
      }
    },
    [user?._id, user?.id, activeTab, limit, location.search]
  );

  useEffect(() => {
    setPage(1);
    fetchSupplierOrders(1);
  }, [activeTab, fetchSupplierOrders]);

  useEffect(() => {
    if (page > 1) {
      fetchSupplierOrders(page);
    }
  }, [page, fetchSupplierOrders]);

  // Deep-link / URL query parameter support for order selection
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const targetOrderId = params.get("orderId") || params.get("highlight");

    if (targetOrderId && orders.length) {
      const found = orders.find(
        (o) => o._id === targetOrderId || o.orderNumber === targetOrderId
      );
      if (found) {
        setSelectedOrder(found);
      }
    }
  }, [location.search, orders]);

  const filteredOrders = orders.filter((order) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      order.orderNumber?.toLowerCase().includes(q) ||
      order._id?.toLowerCase().includes(q) ||
      order.product?.name?.toLowerCase().includes(q) ||
      order.productVariety?.varietyName?.toLowerCase().includes(q)
    );
  });

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-2 sm:px-4 md:px-8 py-3 sm:py-6 mb-16 md:mb-0">
        {/* Pending Penalties Warning Banner */}
        {(user?.isAppAccessBlocked || user?.hasPendingPaymentObligation) && (
          <div className="mb-4 rounded-xl bg-gradient-to-r from-red-50 via-rose-50 to-amber-50 border border-red-200 p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-red-800 animate-fadeIn shadow-xs">
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-ping-slow flex-shrink-0" />
              <span>
                <strong>Account Notice:</strong> You have pending return penalties or restrictions associated with your account.
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate("/supplier-penalties")}
              className="px-3.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-xs whitespace-nowrap shadow-xs transition self-end sm:self-auto"
            >
              View & Pay Penalties
            </button>
          </div>
        )}

        {/* Header Bar */}
        <div
          className={`mb-4 sm:mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
            selectedOrder && window.innerWidth < 768 ? "hidden" : "flex"
          }`}
        >
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-primaryColor flex items-center gap-2">
                <FiShoppingBag /> Live Orders
              </h1>
              <button
                type="button"
                onClick={() => setIsInfoModalOpen(true)}
                className="w-7 h-7 rounded-full bg-gray-100 hover:bg-primaryColor/10 text-gray-500 hover:text-primaryColor flex items-center justify-center transition"
                title="About Live Orders"
              >
                <FiInfo className="text-sm" />
              </button>
            </div>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5">
              Browse buyer requirements matching your linked varieties and submit quotations.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-1.5 p-1 bg-gray-200/70 rounded-xl max-w-full overflow-x-auto">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.label;
              return (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(tab.label)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${isActive
                      ? "bg-white text-primaryColor shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Modal */}
        <SupplierIncomingOrdersInfoModal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
        />

        {/* Loading State */}
        {loading && <AppLoader message="Loading Live Orders..." />}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-red-500 font-medium mb-3">
              Failed to load supplier live orders
            </p>
            <button
              onClick={() => fetchSupplierOrders(page)}
              className="px-4 py-2 bg-primaryColor text-white rounded-xl text-sm font-semibold hover:bg-primaryColor/90 shadow-sm"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && orders.length === 0 && (
          <EmptySupplierOrders activeTab={activeTab} />
        )}

        {/* Orders Layout */}
        {!loading && !error && orders.length > 0 && (
          <>
            {/* ── MOBILE VIEW ──────────────────────────────────────────────── */}
            <div className="md:hidden">
              {!selectedOrder ? (
                <>
                  {/* Search Input */}
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search order ID"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <FiSearch className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Order List */}
                  <div className="space-y-3 mb-4">
                    {filteredOrders.map((order) => (
                      <SupplierOrderListItem
                        key={order._id}
                        order={order}
                        isActive={false}
                        onClick={() => handleOrderSelect(order)}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && pagination.totalPages > 1 && (
                    <Pagination
                      pagination={pagination}
                      page={page}
                      setPage={setPage}
                      limit={limit}
                      setLimit={setLimit}
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Back to list button */}
                  <button
                    onClick={() => {
                      setSelectedOrder(null);
                      navigate("/supplier-orders", { replace: true });
                    }}
                    className="flex items-center gap-2 text-primaryColor mb-3 font-semibold text-sm px-1"
                  >
                    <FiArrowLeft className="w-4 h-4" /> Back to Live Orders
                  </button>

                  {/* Detail Panel */}
                  <div className="bg-transparent sm:bg-white rounded-2xl p-0 sm:p-4 sm:border sm:border-gray-200">
                    <SupplierOrderDetailCard order={selectedOrder} />
                  </div>
                </>
              )}
            </div>

            {/* ── DESKTOP VIEW (Exact matching Buyer Orders grid) ───────────── */}
            <div className="hidden md:block">
              <div className="grid md:grid-cols-12 gap-5">
                {/* Left Sidebar - Orders List */}
                <div className="md:col-span-4 lg:col-span-3 space-y-4 sticky top-16 h-[calc(100vh-4rem)] overflow-hidden">
                  <div className="h-full overflow-y-scroll">
                    {/* Search */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Search order ID"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <FiSearch className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>

                    {/* Orders List */}
                    <div className="space-y-3 mb-3">
                      {filteredOrders.map((order) => (
                        <SupplierOrderListItem
                          key={order._id}
                          order={order}
                          isActive={selectedOrder?._id === order._id}
                          onClick={() => handleOrderSelect(order)}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                      <div className="pt-3 border-t border-gray-200">
                        <Pagination
                          pagination={pagination}
                          page={page}
                          setPage={setPage}
                          limit={limit}
                          setLimit={setLimit}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Panel - Order Detail */}
                <div className="md:col-span-8 lg:col-span-9 bg-white rounded-lg border border-gray-200 p-5">
                  <SupplierOrderDetailCard order={selectedOrder} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupplierOrders;
